// Hosted game nights for companies (MONEY_PLAN.md, Path 1, 2026-10-18): the
// owner runs a one-hour game night for a team over their video call, and the
// team plays on the site from their phones. Everything the public page says
// about the offer lives here, so a price or a game changes in one place and
// hosted-game-nights.test.ts keeps the page honest (every game named exists in
// the catalog, the money adds up).
//
// No checkout: Paddle, the company behind the shop's checkout, sells software
// only, so a live-hosted night is paid by invoice after the date is agreed.

import { MULTILINGUAL_MAX_LANGUAGES } from './multilingual-play';
import { INTL_SITE_LANGUAGES } from './site-translations';

export const HOSTED_NIGHT_PATH = '/hosted-game-nights';

export const HOSTED_NIGHT_OFFER = {
  /**
   * Whole dollars, US. Introductory: $199 (about $13 a head for 15) while we
   * have no reviews or clips; the market lists $399 to $675 for 15 (MONEY_PLAN.md
   * Path 1). Raise after three paid nights with written quotes.
   */
  priceUsd: 199,
  /** People covered by the base price. */
  includedPeople: 15,
  /** Per person above includedPeople, whole dollars. */
  extraPersonUsd: 10,
  /** Shown next to the price while it is the introductory one. */
  introductory: true,
  /** Length of the night in minutes. */
  minutes: 60,
  /** The most people one night takes; bigger groups ask first. */
  maxPeople: 30,
  /** Hours before the night up to which a date can move or be cancelled free. */
  freeChangeHours: 48,
  /** The language the host speaks on the call. */
  hostLanguage: 'English',
  /** Languages a player can use the site in (English plus the translated ones). */
  siteLanguageCount: INTL_SITE_LANGUAGES.length + 1,
  /** Word languages one Pictionary room can mix at the same time. */
  roomWordLanguages: MULTILINGUAL_MAX_LANGUAGES,
  /** Video-call tools the host joins. */
  callTools: ['Zoom', 'Google Meet', 'Microsoft Teams'] as const,
} as const;

/** Catalog ids (src/lib/games-list.ts) in the order the night runs them. */
export const HOSTED_NIGHT_PROGRAMME = [
  { gameId: 'quickMinds', part: 'Warm-up', minutes: 10, why: 'Fast questions with a timer, so everyone is awake and nobody has to speak yet.' },
  { gameId: 'pictionary', part: 'Main game', minutes: 20, why: 'Drawing on a phone. A manager who cannot draw is the best thing that can happen to a team call.' },
  { gameId: 'linkLogic', part: 'Main game', minutes: 20, why: 'Two teams, one-word clues. The one game here that rewards knowing your colleagues well.' },
  { gameId: 'rockPaperScissors', part: 'Finale', minutes: 10, why: 'Rock paper scissors for the whole room at once, down to one winner.' },
] as const;

export const HOSTED_NIGHT_FAQS = [
  { q: 'Does anyone need an account or an app?', a: 'No. Each person opens the site on their phone or laptop and types a room code. Nothing to install, nothing to sign up for.' },
  { q: 'Which video call do you use?', a: `Yours or ours: ${HOSTED_NIGHT_OFFER.callTools.join(', ')}. The host shares the game screen and runs the rounds; your team keeps its cameras on and plays from their own devices.` },
  { q: 'Does the team have to play in English?', a: `No. The host speaks ${HOSTED_NIGHT_OFFER.hostLanguage}, but each person uses the site in their own language (${HOSTED_NIGHT_OFFER.siteLanguageCount} to choose from), and Pictionary can run words in up to ${HOSTED_NIGHT_OFFER.roomWordLanguages} languages in the same room.` },
  { q: 'How many people can join?', a: `The base price covers up to ${HOSTED_NIGHT_OFFER.includedPeople} people, each extra person is $${HOSTED_NIGHT_OFFER.extraPersonUsd}, and one night takes up to ${HOSTED_NIGHT_OFFER.maxPeople}. For a bigger team, ask and we will split the room.` },
  { q: 'Can we pick the games?', a: 'Yes. The programme above is the default, and it works for colleagues who are not close friends. Say in the form if you want something swapped, for example a quiz round about your own company.' },
  { q: 'How do we pay?', a: 'By invoice, after we confirm the date, with an itemised receipt for company expenses across the US, Canada, the UK, Australia, New Zealand, and Europe. There is no payment on this page.' },
  { q: 'What if the date changes?', a: `Move or cancel free up to ${HOSTED_NIGHT_OFFER.freeChangeHours} hours before the night.` },
] as const;

/** What a company's request carries, as written to the contact inbox. */
export type HostedNightRequest = {
  company: string;
  name: string;
  email: string;
  teamSize: number;
  preferredDates: string;
  timeZone: string;
  notes: string;
};

/** Whole-night price for a team of `people`. */
export function hostedNightPriceUsd(people: number): number {
  const extra = Math.max(0, Math.floor(people) - HOSTED_NIGHT_OFFER.includedPeople);
  return HOSTED_NIGHT_OFFER.priceUsd + extra * HOSTED_NIGHT_OFFER.extraPersonUsd;
}

/**
 * The message the contact inbox and the owner's email show. One plain block,
 * because onContactMessageCreated (functions/src/index.ts) mails `message` as
 * it is, and the admin inbox shows it the same way.
 */
export function buildHostedNightMessage(request: HostedNightRequest): string {
  const lines = [
    'Hosted game night request',
    `Company: ${request.company.trim()}`,
    `Team size: ${request.teamSize} (about $${hostedNightPriceUsd(request.teamSize)})`,
    `Preferred dates: ${request.preferredDates.trim()}`,
    `Time zone: ${request.timeZone.trim() || 'not given'}`,
  ];
  const notes = request.notes.trim();
  if (notes) lines.push('', 'Notes:', notes);
  return lines.join('\n');
}
